/**
 * Kiian free-gift — pure eligibility + line-normalization logic.
 *
 * No DOM, no fetch, no storage — everything here is a pure function of a
 * cart-items array + config, so it can be unit tested with plain Node
 * (shopify/tests/gift-manager.test.mjs) and consumed unmodified by the
 * browser (assets/gift-manager.js, loaded as a classic <script> BEFORE
 * gift-manager.js — see snippets/header-actions.liquid).
 *
 * Gift identity (R6/R7/KTD4): a cart line is a gift line if and only if its
 * variant_id matches config.variantId. The `_gift` line-item property is
 * PRESENTATION-ONLY (added so gift-manager.js's own additions are easy to
 * spot in the network tab) and is never read here — a spoofed, missing, or
 * duplicated `_gift` property changes nothing about whether a line is
 * aggregated as "the gift."
 *
 * Nothing here requires a discount allocation or a specific price to
 * recognize the gift (KTD7): the only place `final_line_price` is
 * consulted is to tell an ALREADY-FREE line apart from one that failed to
 * price at $0 (BXGY didn't fire, or a misconfigured native-$0 variant) —
 * a secondary add-success guard, not the gift's identity test. That
 * guard is equally true (and equally harmless) whether the $0 comes from
 * a 100% discount (today) or a native $0 price (post-cutover).
 */

/**
 * Sum of original_line_price (cents) for every cart line EXCLUDING the
 * configured gift variant. This is the JS twin of
 * snippets/offer-subtotal.liquid — the two must stay in lockstep.
 *
 * @param {Array<object>} items - Shopify cart.js `items` array.
 * @param {string|number|null|undefined} variantId - config.variantId.
 * @returns {number} Cents.
 */
function computeOfferSubtotal(items, variantId) {
  const idString = variantId != null && variantId !== '' ? String(variantId) : null;
  const isGiftLine = (item) => idString != null && String(item.variant_id) === idString;

  return items
    .filter((item) => !isGiftLine(item))
    .reduce((sum, item) => sum + (Number(item.original_line_price) || 0), 0);
}

/**
 * Every cart line matching the configured gift variant, identity by
 * variant id alone (R7).
 *
 * @param {Array<object>} items
 * @param {string|number|null|undefined} variantId
 * @returns {Array<object>}
 */
function getGiftLines(items, variantId) {
  const idString = variantId != null && variantId !== '' ? String(variantId) : null;
  if (idString == null) return [];
  return items.filter((item) => String(item.variant_id) === idString);
}

/**
 * Decide what (if anything) needs to change about the cart's gift line(s)
 * for the current snapshot, and whether the 6h "gift offer inactive"
 * stand-down should be set or cleared.
 *
 * Contract (R6/R13): if ineligible or the gift is unavailable, remove
 * EVERY line of the gift variant. If eligible, keep exactly one unit —
 * collapsing duplicate/split/spoofed-property lines and correcting a
 * stray quantity > 1 — regardless of how many lines or what properties
 * they carry.
 *
 * This function is idempotent and safe to call repeatedly against a
 * server cart snapshot: it always returns the FULL set of actions needed
 * to reach the target state from the given snapshot, in a stable order.
 * The caller (assets/gift-manager.js) applies one action at a time and
 * re-derives a fresh plan after each cart mutation, which naturally
 * converges without needing to track incremental state itself.
 *
 * @param {object} params
 * @param {Array<object>} params.items - Shopify cart.js `items` array.
 * @param {object} params.config - { variantId, giftThreshold, giftAvailable }.
 * @param {boolean} [params.giftOfferInactive] - Current 6h stand-down state.
 * @returns {{
 *   offerSubtotal: number,
 *   eligible: boolean,
 *   giftUnavailable: boolean,
 *   actions: Array<{type: 'add'} | {type: 'setQuantity', key: string, quantity: number}>,
 *   standDown: 'set' | 'clear' | null,
 * }}
 */
function decideGiftPlan({ items, config, giftOfferInactive }) {
  const safeItems = Array.isArray(items) ? items : [];
  const variantId = config && config.variantId != null && config.variantId !== '' ? config.variantId : null;
  const threshold = Number.isFinite(config?.giftThreshold) ? config.giftThreshold : Infinity;

  const offerSubtotal = computeOfferSubtotal(safeItems, variantId);
  const giftLines = getGiftLines(safeItems, variantId);

  let inactive = Boolean(giftOfferInactive);
  let standDown = null;

  // Re-arm: falling back under threshold clears a stale stand-down instead
  // of leaving the offer dead until the 6h window expires on its own.
  if (inactive && offerSubtotal < threshold) {
    standDown = 'clear';
    inactive = false;
  }

  // Any already-free gift line anywhere in the cart proves the offer is
  // working again (whichever price model is live); clear a stale stand-down.
  const hasFreeGiftLine = giftLines.some((item) => item.final_line_price === 0);
  if (hasFreeGiftLine && inactive) {
    standDown = 'clear';
    inactive = false;
  }

  const giftAvailable = variantId != null && Boolean(config?.giftAvailable);
  const giftUnavailable = !giftAvailable || inactive;
  const eligible = !giftUnavailable && offerSubtotal >= threshold;

  const actions = [];

  if (!eligible) {
    // Ineligible or unavailable: no gift line may survive, regardless of
    // price, quantity, or properties.
    giftLines.forEach((item) => {
      if (item.quantity !== 0) actions.push({ type: 'setQuantity', key: item.key, quantity: 0 });
    });
    return { offerSubtotal, eligible, giftUnavailable, actions, standDown };
  }

  if (giftLines.length === 0) {
    return { offerSubtotal, eligible, giftUnavailable, actions: [{ type: 'add' }], standDown };
  }

  const freeLine = giftLines.find((item) => item.final_line_price === 0);

  if (!freeLine) {
    // Every existing line of the gift variant is priced (BXGY failed to
    // apply, or a native-$0 variant is misconfigured with a real price).
    // Fail closed: remove them all and stand down rather than let a
    // shopper end up paying for what was promised free.
    giftLines.forEach((item) => actions.push({ type: 'setQuantity', key: item.key, quantity: 0 }));
    return { offerSubtotal, eligible, giftUnavailable, actions, standDown: 'set' };
  }

  // Keep exactly one unit of the (first) free line; remove every other
  // line of the variant, whatever its price, quantity, or properties.
  giftLines.forEach((item) => {
    if (item.key === freeLine.key) {
      if (item.quantity !== 1) actions.push({ type: 'setQuantity', key: item.key, quantity: 1 });
    } else {
      actions.push({ type: 'setQuantity', key: item.key, quantity: 0 });
    }
  });

  return { offerSubtotal, eligible, giftUnavailable, actions, standDown };
}

const KiianGiftLogic = { computeOfferSubtotal, getGiftLines, decideGiftPlan };

// Guarded UMD-style export (Shopify gotcha, docs/plans/... — this file is
// loaded as a plain classic <script>, NOT type="module", so `export` syntax
// would throw in the browser; `module` is simply undefined there, which
// `typeof` safely detects without throwing).
if (typeof module !== 'undefined' && module.exports) {
  module.exports = KiianGiftLogic;
} else if (typeof self !== 'undefined') {
  self.KiianGiftLogic = KiianGiftLogic;
}
